import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { create } from 'zustand';
import { apiClient, getAccessToken } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsListParams {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── WebSocket Store ─────────────────────────────────────────────────────────

interface WebSocketState {
  /** Whether the WebSocket is currently connected */
  isConnected: boolean;
  /** Number of unread notifications (updated in real-time) */
  unreadCount: number;
  /** Set connection status */
  setConnected: (connected: boolean) => void;
  /** Set unread count */
  setUnreadCount: (count: number) => void;
  /** Increment unread count by 1 */
  incrementUnread: () => void;
  /** Decrement unread count by a given amount */
  decrementUnread: (amount: number) => void;
}

export const useNotificationStore = create<WebSocketState>((set) => ({
  isConnected: false,
  unreadCount: 0,
  setConnected: (connected) => set({ isConnected: connected }),
  setUnreadCount: (count) => set({ unreadCount: count }),
  incrementUnread: () => set((state) => ({ unreadCount: state.unreadCount + 1 })),
  decrementUnread: (amount) =>
    set((state) => ({ unreadCount: Math.max(0, state.unreadCount - amount) })),
}));

// ─── Query Keys ──────────────────────────────────────────────────────────────

const notificationKeys = {
  all: ['notifications'] as const,
  list: (params: NotificationsListParams) => [...notificationKeys.all, 'list', params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Hook to fetch paginated notifications list.
 * Validates: Requirements 13.5
 */
export function useNotifications(params: NotificationsListParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.unreadOnly) queryParams.set('unreadOnly', 'true');

      const response = await apiClient.get<ApiResponse<PaginatedResponse<Notification>>>(
        `/notifications?${queryParams.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to fetch the unread notification count.
 * Used by the header notification bell badge.
 */
export function useUnreadCount() {
  const { setUnreadCount } = useNotificationStore();

  const hasToken = !!getAccessToken();

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<{ count: number }>>(
        '/notifications/unread-count',
      );
      const count = response.data.data.count;
      setUnreadCount(count);
      return count;
    },
    // Skip when there's no access token (logged out / expired session) to
    // avoid 401 requests hitting the API.
    enabled: hasToken,
    refetchInterval: hasToken ? 60000 : false, // Fallback polling every 60s (WebSocket handles real-time)
  });
}

/**
 * Hook to mark a single notification as read.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { decrementUnread } = useNotificationStore();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await apiClient.patch(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      decrementUnread(1);
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Hook to mark all notifications as read.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { setUnreadCount } = useNotificationStore();

  return useMutation({
    mutationFn: async () => {
      await apiClient.patch('/notifications/read-all');
    },
    onSuccess: () => {
      setUnreadCount(0);
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

// ─── WebSocket Hook ──────────────────────────────────────────────────────────

/**
 * Hook that establishes a WebSocket connection for real-time notification delivery.
 * Automatically reconnects on disconnection with exponential backoff.
 * Validates: Requirements 13.5
 */
export function useNotificationWebSocket() {
  const queryClient = useQueryClient();
  const { setConnected, incrementUnread } = useNotificationStore();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;

  const connect = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    // Determine WebSocket URL from current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/notifications?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as {
            type: string;
            payload?: Notification;
          };

          switch (message.type) {
            case 'notification':
              // New notification received — increment badge and invalidate queries
              incrementUnread();
              queryClient.invalidateQueries({ queryKey: notificationKeys.all });
              break;
            case 'ping':
              // Respond to server ping with pong
              ws.send(JSON.stringify({ type: 'pong' }));
              break;
          }
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = (event) => {
        setConnected(false);
        wsRef.current = null;

        // Don't reconnect if closed intentionally (code 1000) or max attempts reached
        if (event.code === 1000 || reconnectAttemptsRef.current >= maxReconnectAttempts) {
          return;
        }

        // Exponential backoff: 1s, 2s, 4s, 8s, ... up to 30s
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      };

      ws.onerror = () => {
        // Error will trigger onclose, which handles reconnection
        ws.close();
      };
    } catch {
      // WebSocket construction failed — schedule reconnect
      setConnected(false);
      const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
      reconnectAttemptsRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    }
  }, [setConnected, incrementUnread, queryClient]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounted');
      wsRef.current = null;
    }
    setConnected(false);
  }, [setConnected]);

  useEffect(() => {
    connect();
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected: useNotificationStore((s) => s.isConnected),
    disconnect,
    reconnect: connect,
  };
}
